/**
 * Subscription Polling Utility
 * 
 * Polls the subscription endpoint to wait for webhook completion
 * Works for ANY addon type - universal solution
 * 
 * Usage:
 * const result = await pollForSubscriptions({
 *   mechanicId: 123,
 *   shopId: 456,
 *   expectedPlanIds: [5, 7, 9],
 *   maxAttempts: 20,
 *   intervalMs: 500
 * });
 * 
 * if (result.success) {
 *   // All expected subscriptions found!
 * } else if (result.timeout) {
 *   // Need to trigger reconciliation
 * }
 */

import getApiBase from '../components/getApiBase';

const API = getApiBase();

/**
 * Poll subscription endpoint until all expected plans are present
 * 
 * @param {Object} options - Polling options
 * @param {number} options.mechanicId - Mechanic user ID
 * @param {number} options.shopId - Shop ID
 * @param {number[]} options.expectedPlanIds - Array of plan IDs to wait for
 * @param {number} options.maxAttempts - Maximum polling attempts (default: 20)
 * @param {number} options.intervalMs - Milliseconds between polls (default: 500)
 * @returns {Promise<Object>} Result object with success/timeout status
 */
export const pollForSubscriptions = async ({
  mechanicId,
  shopId,
  expectedPlanIds,
  maxAttempts = 20, // 10 seconds total with 500ms interval
  intervalMs = 500,
}) => {
  console.log(`🔍 Polling for subscriptions: expecting plan IDs [${(expectedPlanIds || []).join(', ') || 'any paid plan'}]`);

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      // Wait before polling (not on first attempt)
      if (attempt > 1) {
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }

      // Fetch current subscriptions
      const response = await fetch(
        `${API}/api/mechanicProfile/subscription?mechanic_id=${mechanicId}&shop_id=${shopId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        console.warn(`⚠️ Polling attempt ${attempt}/${maxAttempts}: API error ${response.status}`);
        continue;
      }

      const data = await response.json();
      const currentPlanIds = (data.subscriptions || []).map(sub => sub.plan?.id).filter(Boolean);

      // When no specific plans expected (checkout return), wait for any paid subscription
      if (!expectedPlanIds || expectedPlanIds.length === 0) {
        if (data.hasPaidSubscription) {
          console.log(`✅ Paid subscription detected after ${attempt} attempt(s)`);
          return {
            success: true,
            subscriptions: data.subscriptions,
            attempts: attempt,
            duration: attempt * intervalMs,
          };
        }
      } else {
        // Check if ALL expected plans are present
        const allFound = expectedPlanIds.every(expectedId => currentPlanIds.includes(expectedId));

        if (allFound) {
          console.log(`✅ All subscriptions found after ${attempt} attempt(s) (~${attempt * intervalMs / 1000}s)`);
          return {
            success: true,
            subscriptions: data.subscriptions,
            attempts: attempt,
            duration: attempt * intervalMs,
          };
        }

        const missingPlanIds = expectedPlanIds.filter(id => !currentPlanIds.includes(id));
        console.log(
          `⏳ Attempt ${attempt}/${maxAttempts}: Still waiting for plan IDs [${missingPlanIds.join(', ')}]`
        );
        continue;
      }

      console.log(`⏳ Attempt ${attempt}/${maxAttempts}: Waiting for paid subscription...`);
    } catch (error) {
      console.error(`❌ Polling attempt ${attempt}/${maxAttempts} failed:`, error);
      // Continue polling even on error (network might recover)
    }
  }

  // Timeout after max attempts
  console.warn(`⏱️ Polling timeout after ${maxAttempts} attempts (~${maxAttempts * intervalMs / 1000}s)`);
  return {
    success: false,
    timeout: true,
    attempts: maxAttempts,
    duration: maxAttempts * intervalMs,
  };
};

/**
 * Sync subscription state from a completed Stripe Checkout session (webhook fallback)
 */
export const syncCheckoutSession = async (sessionId, mechanicId, shopId) => {
  console.log(`🔄 Syncing checkout session ${sessionId} for mechanic_id=${mechanicId}, shop_id=${shopId}`);

  try {
    const response = await fetch(`${API}/api/stripe/sync-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      credentials: 'include',
      body: JSON.stringify({ sessionId, mechanicId, shopId }),
    });

    const data = await response.json();

    if (response.ok) {
      console.log(`✅ Checkout session sync successful: ${data.subscriptions?.length || 0} subscriptions`);
      return {
        success: true,
        planIds: data.planIds || [],
        subscriptions: data.subscriptions,
        billingReadyForQuotes: data.billingReadyForQuotes,
        message: data.message,
      };
    }

    console.error('❌ Checkout session sync failed:', data.error);
    return {
      success: false,
      error: data.error || 'Checkout session sync failed',
    };
  } catch (error) {
    console.error('❌ Checkout session sync request failed:', error);
    return {
      success: false,
      error: error.message || 'Network error during checkout sync',
    };
  }
};

/**
 * Trigger manual reconciliation with Stripe
 * Used as fallback when polling times out
 * 
 * @param {number} mechanicId - Mechanic user ID
 * @param {number} shopId - Shop ID
 * @returns {Promise<Object>} Reconciliation result
 */
export const reconcileSubscriptions = async (mechanicId, shopId) => {
  console.log(`🔄 Triggering subscription reconciliation for mechanic_id=${mechanicId}, shop_id=${shopId}`);

  try {
    const response = await fetch(`${API}/api/stripe/reconcile-subscriptions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      credentials: 'include',
      body: JSON.stringify({ mechanicId, shopId }),
    });

    const data = await response.json();

    if (response.ok) {
      console.log(`✅ Reconciliation successful: ${data.subscriptions?.length || 0} subscriptions synced`);
      return {
        success: true,
        subscriptions: data.subscriptions,
        message: data.message,
      };
    } else {
      console.error('❌ Reconciliation failed:', data.error);
      return {
        success: false,
        error: data.error || 'Reconciliation failed',
      };
    }
  } catch (error) {
    console.error('❌ Reconciliation request failed:', error);
    return {
      success: false,
      error: error.message || 'Network error during reconciliation',
    };
  }
};

/**
 * Complete subscription update flow with polling and fallback
 * 
 * @param {Object} options - Update options
 * @param {number} options.mechanicId - Mechanic user ID
 * @param {number} options.shopId - Shop ID
 * @param {number[]} options.expectedPlanIds - Array of plan IDs to wait for
 * @param {Function} options.onProgress - Optional callback for progress updates
 * @returns {Promise<Object>} Final result
 */
export const waitForSubscriptionUpdate = async ({
  mechanicId,
  shopId,
  expectedPlanIds,
  sessionId,
  onProgress,
}) => {
  // Phase 0: Sync from checkout session when returning from Stripe (preview webhook gap)
  if (sessionId) {
    if (onProgress) onProgress({ phase: 'syncing', message: 'Confirming payment...' });
    const syncResult = await syncCheckoutSession(sessionId, mechanicId, shopId);
    if (syncResult.success) {
      if (syncResult.planIds?.length) {
        expectedPlanIds = syncResult.planIds;
      }
      const syncedPlanIds = (syncResult.subscriptions || [])
        .map((sub) => sub.plan?.id)
        .filter(Boolean);
      const hasPaidPlan = (syncResult.subscriptions || []).some(
        (sub) =>
          sub.status === 'active' &&
          sub.plan &&
          sub.plan.plan_type !== 'base_tier' &&
          parseFloat(sub.plan.monthly_price || 0) > 0,
      );
      const plansMatch =
        !expectedPlanIds?.length ||
        expectedPlanIds.every((id) => syncedPlanIds.includes(id));
      if (hasPaidPlan && plansMatch) {
        if (onProgress) onProgress({ phase: 'complete', message: 'Subscriptions updated!' });
        return {
          success: true,
          subscriptions: syncResult.subscriptions,
          billingReadyForQuotes: syncResult.billingReadyForQuotes,
          method: 'checkout_session_sync',
        };
      }
      if (syncResult.billingReadyForQuotes) {
        if (onProgress) onProgress({ phase: 'complete', message: 'Payment method saved.' });
        return {
          success: true,
          subscriptions: syncResult.subscriptions,
          billingReadyForQuotes: true,
          method: 'checkout_session_sync',
        };
      }
    }
  }

  // Phase 1: Poll for webhook completion
  if (onProgress) onProgress({ phase: 'polling', message: 'Waiting for subscription update...' });

  const pollResult = await pollForSubscriptions({
    mechanicId,
    shopId,
    expectedPlanIds,
  });

  if (pollResult.success) {
    if (onProgress) onProgress({ phase: 'complete', message: 'Subscriptions updated!' });
    return {
      success: true,
      subscriptions: pollResult.subscriptions,
      method: 'webhook',
      duration: pollResult.duration,
    };
  }

  // Phase 2: Polling timeout - trigger reconciliation
  console.warn('⚠️ Polling timed out, attempting reconciliation...');
  if (onProgress) onProgress({ phase: 'reconciling', message: 'Syncing with Stripe...' });

  const reconcileResult = await reconcileSubscriptions(mechanicId, shopId);

  if (reconcileResult.success) {
    // Verify all expected plans are now present
    const currentPlanIds = (reconcileResult.subscriptions || []).map(sub => sub.plan?.id).filter(Boolean);
    const allFound = expectedPlanIds?.length
      ? expectedPlanIds.every(id => currentPlanIds.includes(id))
      : (reconcileResult.subscriptions || []).some(
          (sub) => sub.plan && sub.plan.plan_type !== 'base_tier' && parseFloat(sub.plan.monthly_price || 0) > 0
        );

    if (allFound) {
      if (onProgress) onProgress({ phase: 'complete', message: 'Subscriptions synchronized!' });
      return {
        success: true,
        subscriptions: reconcileResult.subscriptions,
        method: 'reconciliation',
        duration: pollResult.duration,
      };
    } else {
      console.error('❌ Reconciliation succeeded but expected plans still missing');
      const missingIds = expectedPlanIds.filter(id => !currentPlanIds.includes(id));
      return {
        success: false,
        error: `Plans still missing after reconciliation: ${missingIds.join(', ')}`,
        subscriptions: reconcileResult.subscriptions,
      };
    }
  }

  // Both polling and reconciliation failed
  if (onProgress) onProgress({ phase: 'error', message: 'Subscription update failed' });
  return {
    success: false,
    error: reconcileResult.error || 'Failed to update subscriptions',
  };
};

export default {
  pollForSubscriptions,
  reconcileSubscriptions,
  syncCheckoutSession,
  waitForSubscriptionUpdate,
};

